import type {
  WorkspaceBillingActivityPageContract,
  WorkspaceBillingActivityType,
  WorkspaceBillingContract,
  WorkspaceWalletContract,
} from '@nest-react/domain';
import type { StateCreator } from 'zustand';

import { ApiRequestError, requestWithActiveRegionalClient } from '~/app/regionalApiClient';

import type { WorkspaceStore } from '../workspaceStore';
import {
  emptyBillingActivitySnapshot,
  emptyBillingSnapshot,
  type WorkspaceBillingSnapshot,
} from '../billingTypes';

export type BillingSlice = {
  billingByWorkspace: Record<string, WorkspaceBillingSnapshot>;
  fetchWorkspaceWallet: (workspaceId: string) => Promise<void>;
  fetchWorkspaceBillingSummary: (workspaceId: string) => Promise<void>;
  fetchWorkspaceBillingActivity: (
    workspaceId: string,
    options: {
      type: WorkspaceBillingActivityType | null;
      cursor: string | null;
    }
  ) => Promise<'invalid-cursor' | void>;
  setWorkspaceWalletBalance: (
    workspaceId: string,
    balance: string | null
  ) => void;
  resetWorkspaceBilling: (workspaceId?: string) => void;
};

const patchBilling = (
  current: Record<string, WorkspaceBillingSnapshot>,
  workspaceId: string,
  patch: Partial<WorkspaceBillingSnapshot>
): Record<string, WorkspaceBillingSnapshot> => ({
  ...current,
  [workspaceId]: {
    ...(current[workspaceId] ?? emptyBillingSnapshot()),
    ...patch,
  },
});

const patchActivity = (
  current: Record<string, WorkspaceBillingSnapshot>,
  workspaceId: string,
  patch: Partial<WorkspaceBillingSnapshot['activity']>
): Record<string, WorkspaceBillingSnapshot> => {
  const bucket = current[workspaceId] ?? emptyBillingSnapshot();
  return patchBilling(current, workspaceId, {
    activity: {
      ...bucket.activity,
      ...patch,
    },
  });
};

const summaryRequestByWorkspace = new Map<string, number>();
const activityRequestByWorkspace = new Map<string, number>();

export const createBillingSlice: StateCreator<
  WorkspaceStore,
  [],
  [],
  BillingSlice
> = set => ({
  billingByWorkspace: {},
  fetchWorkspaceWallet: async workspaceId => {
    set(state => ({
      billingByWorkspace: patchBilling(state.billingByWorkspace, workspaceId, {
        walletLoading: true,
      }),
    }));
    try {
      const wallet = await requestWithActiveRegionalClient<WorkspaceWalletContract>(
        `/workspaces/${workspaceId}/wallet`
      );
      set(state => ({
        billingByWorkspace: patchBilling(
          state.billingByWorkspace,
          workspaceId,
          {
            balance: wallet.availableBalance ?? wallet.balance,
            walletLoading: false,
            walletLoaded: true,
          }
        ),
      }));
    } catch {
      set(state => ({
        billingByWorkspace: patchBilling(
          state.billingByWorkspace,
          workspaceId,
          {
            balance: null,
            walletLoading: false,
            walletLoaded: true,
          }
        ),
      }));
    }
  },
  fetchWorkspaceBillingSummary: async workspaceId => {
    const requestId = (summaryRequestByWorkspace.get(workspaceId) ?? 0) + 1;
    summaryRequestByWorkspace.set(workspaceId, requestId);
    set(state => ({
      billingByWorkspace: patchBilling(state.billingByWorkspace, workspaceId, {
        summaryLoading: true,
        summaryError: null,
      }),
    }));
    try {
      const summary =
        await requestWithActiveRegionalClient<WorkspaceBillingContract>(
          `/workspaces/${workspaceId}/billing`
        );
      if (summaryRequestByWorkspace.get(workspaceId) !== requestId) return;
      set(state => ({
        billingByWorkspace: patchBilling(
          state.billingByWorkspace,
          workspaceId,
          {
            summary,
            summaryLoading: false,
            summaryLoaded: true,
            summaryError: null,
          }
        ),
      }));
    } catch (reason) {
      if (summaryRequestByWorkspace.get(workspaceId) !== requestId) return;
      set(state => ({
        billingByWorkspace: patchBilling(
          state.billingByWorkspace,
          workspaceId,
          {
            summary: null,
            summaryLoading: false,
            summaryLoaded: true,
            summaryError:
              reason instanceof Error ? reason.message : 'Billing could not be loaded.',
          }
        ),
      }));
    }
  },
  fetchWorkspaceBillingActivity: async (workspaceId, { type, cursor }) => {
    const requestId = (activityRequestByWorkspace.get(workspaceId) ?? 0) + 1;
    activityRequestByWorkspace.set(workspaceId, requestId);
    set(state => ({
      billingByWorkspace: patchActivity(state.billingByWorkspace, workspaceId, {
        loading: true,
        error: null,
        type,
        cursor,
      }),
    }));
    try {
      const params = new URLSearchParams();
      if (type) params.set('type', type);
      if (cursor) params.set('cursor', cursor);
      const query = params.toString();
      const page =
        await requestWithActiveRegionalClient<WorkspaceBillingActivityPageContract>(
          `/workspaces/${workspaceId}/billing/activity${query ? `?${query}` : ''}`
        );
      if (activityRequestByWorkspace.get(workspaceId) !== requestId) return undefined;
      set(state => ({
        billingByWorkspace: patchActivity(state.billingByWorkspace, workspaceId, {
          page,
          loading: false,
          error: null,
          type,
          cursor,
        }),
      }));
      return undefined;
    } catch (reason) {
      if (activityRequestByWorkspace.get(workspaceId) !== requestId) return undefined;
      if (reason instanceof ApiRequestError && reason.status === 400 && cursor) {
        set(state => ({
          billingByWorkspace: patchActivity(
            state.billingByWorkspace,
            workspaceId,
            {
              loading: false,
              error: null,
            }
          ),
        }));
        return 'invalid-cursor';
      }
      set(state => ({
        billingByWorkspace: patchActivity(state.billingByWorkspace, workspaceId, {
          loading: false,
          error:
            reason instanceof Error
              ? reason.message
              : 'Billing activity could not be loaded.',
          type,
          cursor,
        }),
      }));
      return undefined;
    }
  },
  setWorkspaceWalletBalance: (workspaceId, balance) => {
    set(state => ({
      billingByWorkspace: patchBilling(state.billingByWorkspace, workspaceId, {
        balance,
        walletLoaded: true,
        walletLoading: false,
      }),
    }));
  },
  resetWorkspaceBilling: workspaceId => {
    if (!workspaceId) {
      summaryRequestByWorkspace.clear();
      activityRequestByWorkspace.clear();
      set({ billingByWorkspace: {} });
      return;
    }
    summaryRequestByWorkspace.delete(workspaceId);
    activityRequestByWorkspace.delete(workspaceId);
    set(state => {
      const next = { ...state.billingByWorkspace };
      delete next[workspaceId];
      return { billingByWorkspace: next };
    });
  },
});
